import { useCallback, useMemo, useState } from 'react'

import { type TagDefinition, collectDescendantIds } from '../../../../../shared/tags/tagRegistry'
import { apiService } from '../../../services/api'

export function useTagRegistryState(bookId?: string, selectedTagIds?: string[]) {
  const [tagColorMap, setTagColorMap] = useState<Record<string, string>>({})
  const [tagUsageByChapter, setTagUsageByChapter] = useState<Record<string, Record<string, number>>>({})
  const [tagRegistry, setTagRegistry] = useState<Record<string, TagDefinition>>({})
  const [tagRegistryLoaded, setTagRegistryLoaded] = useState(false)

  const applyTagRegistry = useCallback((tags: Record<string, TagDefinition>) => {
    setTagRegistry(tags)
    const map: Record<string, string> = {}
    for (const [key, meta] of Object.entries(tags)) {
      if (meta.color) {
        map[key] = meta.color
      }
    }
    setTagColorMap(map)
  }, [])

  const fetchTagData = useCallback(async () => {
    if (!bookId) {
      return
    }
    try {
      const [registryRes, usageRes] = await Promise.all([
        apiService.getTagRegistry(bookId),
        apiService.getTagUsageCounts(bookId),
      ])
      const tags = registryRes.data.tags as Record<string, TagDefinition>
      applyTagRegistry(tags)
      setTagUsageByChapter(usageRes.data)
      setTagRegistryLoaded(true)
    } catch {
      // Non-critical.
    }
  }, [applyTagRegistry, bookId])

  const tagUsageFlatCounts = useMemo(() => {
    const flat: Record<string, number> = {}
    for (const [tag, chapterCounts] of Object.entries(tagUsageByChapter)) {
      let sum = 0
      for (const count of Object.values(chapterCounts)) {
        sum += count
      }
      flat[tag] = sum
    }
    return flat
  }, [tagUsageByChapter])

  const tagMatchedChapters = useMemo(() => {
    if (!selectedTagIds?.length) {
      return new Set<string>()
    }
    const matched = new Set<string>()
    const allRelevantIds = new Set<string>()
    for (const tagId of selectedTagIds) {
      for (const id of collectDescendantIds(tagRegistry, tagId)) {
        allRelevantIds.add(id)
      }
    }
    for (const [tag, chapterCounts] of Object.entries(tagUsageByChapter)) {
      if (allRelevantIds.has(tag)) {
        for (const filename of Object.keys(chapterCounts)) {
          matched.add(filename)
        }
      }
    }
    return matched
  }, [selectedTagIds, tagUsageByChapter, tagRegistry])

  return {
    tagColorMap,
    tagRegistry,
    tagRegistryLoaded,
    tagUsageFlatCounts,
    tagMatchedChapters,
    applyTagRegistry,
    fetchTagData,
  }
}
